import { Injectable, computed, signal } from '@angular/core'
import {
  ChatEntity,
  ChatListItem,
  ChatMessageEntity,
  UserSearchResult,
} from '../interfaces/chat.interface'

@Injectable({ providedIn: 'root' })
export class MessageChatStateService {
  private readonly typingTimeouts = new Map<number, ReturnType<typeof setTimeout>>()
  readonly chats = signal<ChatEntity[]>([])
  readonly selectedChatId = signal<number | null>(null)
  readonly messages = signal<ChatMessageEntity[]>([])
  readonly onlineUserIds = signal<Set<number>>(new Set())
  readonly typingUsers = signal<Map<number, { userId: number; userName: string }>>(new Map())
  readonly searchTerm = signal('')
  readonly loading = signal(false)
  readonly loadingMessages = signal(false)
  readonly loadingMore = signal(false)
  readonly hasMoreMessages = signal(true)
  readonly currentUserId = signal<number>(0)
  readonly currentUserName = signal<string>('')
  readonly userSearchResults = signal<UserSearchResult[]>([])
  readonly searchingUsers = signal(false)

  // Set by external components (e.g. global search) to open a direct chat
  // with a specific user. Cleared by message-chat after handling.
  readonly pendingOpenUser = signal<UserSearchResult | null>(null)

  readonly selectedChat = computed(() => {
    const id = this.selectedChatId()
    return this.chats().find((c) => c.id === id) ?? null
  })

  readonly chatList = computed<ChatListItem[]>(() => {
    const userId = this.currentUserId()
    const online = this.onlineUserIds()

    return this.chats().map((chat) => {
      const item: ChatListItem = { ...chat }

      if (chat.type === 'PRIVATE') {
        const other = chat.participants?.find((p) => p.userId !== userId)
        item.otherUserName = other?.userName ?? chat.name
        item.initials = this.getInitials(item.otherUserName)
        item.isOnline = other ? online.has(other.userId) : false
      } else {
        item.initials = this.getInitials(chat.name)
        item.isOnline = false
      }

      return item
    })
  })

  readonly filteredChatList = computed(() => {
    const term = this.searchTerm().toLowerCase().trim()
    if (!term) {
      return this.chatList()
    }
    return this.chatList().filter((chat) => {
      const name = chat.type === 'PRIVATE' ? (chat.otherUserName ?? '') : chat.name
      return name.toLowerCase().includes(term)
    })
  })

  readonly filteredUserResults = computed(() => {
    const currentId = this.currentUserId()
    const existingUserIds = new Set(
      this.chats()
        .filter((c) => c.type === 'PRIVATE')
        .flatMap((c) => c.participants)
        .filter((p) => p.userId !== currentId)
        .map((p) => p.userId)
    )

    return this.userSearchResults()
      .filter((u) => u.userId !== currentId)
      .filter((u) => !existingUserIds.has(u.userId))
  })

  readonly sortedMessages = computed(() => {
    return [...this.messages()].sort(
      (a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime()
    )
  })

  readonly chatImages = computed(() => {
    return this.sortedMessages()
      .flatMap((m) => m.attachments)
      .filter((a) => a.mimeType.startsWith('image/'))
  })

  readonly typingUsersForChat = computed(() => {
    const chatId = this.selectedChatId()
    if (!chatId) {
      return []
    }
    const currentUserId = this.currentUserId()
    return Array.from(this.typingUsers().values()).filter((u) => u.userId !== currentUserId)
  })

  readonly totalUnread = computed(() =>
    this.chats().reduce((sum, c) => sum + (c.unreadCount ?? 0), 0)
  )

  addMessage(message: ChatMessageEntity): void {
    this.messages.update((msgs) => [...msgs, message])
  }

  updateMessage(updated: ChatMessageEntity): void {
    this.messages.update((msgs) => msgs.map((m) => (m.id === updated.id ? updated : m)))
  }

  deleteMessage(messageId: number): void {
    this.messages.update((msgs) =>
      msgs.map((m) =>
        m.id === messageId ? { ...m, deleted: true, content: null, attachments: [] } : m
      )
    )
  }

  prependMessages(olderMessages: ChatMessageEntity[]): void {
    this.messages.update((msgs) => [...olderMessages, ...msgs])
  }

  setUserOnline(userId: number): void {
    this.onlineUserIds.update((set) => {
      const next = new Set(set)
      next.add(userId)
      return next
    })
  }

  setUserOffline(userId: number): void {
    this.onlineUserIds.update((set) => {
      const next = new Set(set)
      next.delete(userId)
      return next
    })
  }

  setTyping(chatId: number, userId: number, userName: string): void {
    this.typingUsers.update((map) => {
      const next = new Map(map)
      next.set(userId, { userId, userName })
      return next
    })

    const existing = this.typingTimeouts.get(userId)
    if (existing) {
      clearTimeout(existing)
    }

    const timeout = setTimeout(() => {
      this.clearTyping(userId)
    }, 10000)
    this.typingTimeouts.set(userId, timeout)
  }

  clearTyping(userId: number): void {
    const timeout = this.typingTimeouts.get(userId)
    if (timeout) {
      clearTimeout(timeout)
      this.typingTimeouts.delete(userId)
    }

    this.typingUsers.update((map) => {
      const next = new Map(map)
      next.delete(userId)
      return next
    })
  }

  clearAllTyping(): void {
    for (const timeout of this.typingTimeouts.values()) {
      clearTimeout(timeout)
    }
    this.typingTimeouts.clear()
    this.typingUsers.set(new Map())
  }

  resetUnreadCount(chatId: number): void {
    this.chats.update((chats) =>
      chats.map((c) => (c.id === chatId ? { ...c, unreadCount: 0 } : c))
    )
  }

  updateChatLastMessage(chatId: number, message: ChatMessageEntity): void {
    this.chats.update((chats) =>
      chats.map((c) =>
        c.id === chatId
          ? {
              ...c,
              lastMessage: message,
              unreadCount: this.selectedChatId() === chatId ? c.unreadCount : c.unreadCount + 1,
            }
          : c
      )
    )
  }

  private getInitials(name: string): string {
    if (!name) {
      return '?'
    }
    const parts = name.trim().split(' ')
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return parts[0][0]?.toUpperCase() ?? '?'
  }
}
